import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Button, Card, Col, Divider, Row, Typography, message, Tag, Alert } from "antd";
import { useSelector } from "react-redux";
import { ExportOutlined } from "@ant-design/icons";
import moment from "moment";
import { withPatientReferenceParams } from "../../services/zoomSchedulerLinks";
import fertilityImage from "../../assets/images/auth/fertilityImage.svg";
import "../Register/Register.css";
import "../Register/SignUpPages/SignupPages.css";

const { Title, Text } = Typography;

function parseAppointmentDateTime(confirmation) {
  if (!confirmation?.date || !confirmation?.startTime) return null;
  const dt = new Date(`${confirmation.date}T${confirmation.startTime}:00`);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

const accentBtn = {
  height: "46px",
  borderRadius: "8px",
  fontWeight: 600,
  fontSize: "16px",
  color: "#ffffff",
  backgroundColor: "#00ADEF",
  border: "1px solid #00ADEF",
};

export default function QuickBookingConfirmation() {
  const navigate = useNavigate();
  const userAuth = useSelector((state) => state?.authentication?.userAuth);
  
  const confirmation = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("quickBookingConfirmation") || "null");
    } catch (e) {
      return null;
    }
  }, []);

  const providerMeta = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("quickBookingProvider") || "null");
    } catch (e) {
      return null;
    }
  }, []);

  const [hoursUntil, setHoursUntil] = useState(null);

  useEffect(() => {
    
    if (!providerMeta) navigate("/quick-start");
  }, [providerMeta, navigate]);

  useEffect(() => {
    const dt = parseAppointmentDateTime(confirmation);
    if (!dt) return;
    const diffMs = dt.getTime() - Date.now();
    setHoursUntil(diffMs / (1000 * 60 * 60));
  }, [confirmation]);

  const token = userAuth?.obj?.token;
  
  const paymentOverdue = hoursUntil === null ? false : hoursUntil < 72;

  const onCompletePayment = () => {
    if (!token) {
      message.info("Please sign in to complete payment.");
      navigate("/");
      return;
    }
    navigate("/plans");
  };

  // Reopen the same scheduler link, in case the patient closed the tab
  // before finishing on Zoom's page.
  const patientForReopen = useMemo(() => {
    try {
      const draft = JSON.parse(localStorage.getItem("quickPatientDraft") || "null");
      const quickResp = draft?.quickSignUpResponse || {};
      return {
        firstName: draft?.firstName,
        lastName: draft?.lastName,
        email: draft?.email,
        id: quickResp?.patientId ?? quickResp?.PatientId ?? quickResp?.id ?? quickResp?.Id,
      };
    } catch (e) {
      return null;
    }
  }, []);

  const reopenSchedulerUrl = useMemo(
    () => withPatientReferenceParams(providerMeta?.bookingLink, patientForReopen),
    [providerMeta, patientForReopen],
  );

  const whenLabel =
    confirmation?.date && confirmation?.startTime
      ? `${moment(confirmation.date).format("dddd, MMMM D, YYYY")} · ${confirmation.startTime} – ${confirmation.endTime || ""}`
      : null;

  if (!providerMeta) {
    return null;
  }

  return (
    <div className="">
      <div className="full-width-container">
        <Row gutter={24} style={{ minHeight: "100vh" }}>
          <Col span={2} className="column-1" style={{ backgroundColor: "#EFD0BD" }} />
          <Col span={20} className="column-2">
            <div className="form-scrollable">
              <div className="col-xl-9 col-lg-10 col-md-12">
                <nav className="navbar mt-3">
                  <img
                    className="float-left"
                    src={fertilityImage}
                    alt="myfertility"
                    style={{ width: "150px" }}
                  />
                  <NavLink
                    to="/"
                    className="btn-user btn-block"
                    style={{
                      ...accentBtn,
                      minWidth: "126px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0 20px",
                      lineHeight: 1,
                      textDecoration: "none",
                    }}
                  >
                    Sign In
                  </NavLink>
                </nav>

                <div className="card o-hidden border-0 my-3">
                  <div className="card-body p-4 p-md-5">
                    <div style={{ maxWidth: 960, margin: "0 auto" }}>
                      <Title level={3} style={{ marginBottom: 8, color: "#000" }}>
                        Finish booking on Zoom
                      </Title>
                      <Text type="secondary" style={{ fontSize: 15 }}>
                        Your appointment isn&apos;t locked in until you pick a time on the scheduling
                        page - the steps below keep everything else on track in the meantime.
                      </Text>

                      <Alert
                        type="info"
                        showIcon
                        style={{ marginTop: 16 }}
                        message="Already picked a time and submitted it on Zoom?"
                        description="You're all set - you'll get a confirmation email from Zoom with your appointment details. If the tab didn't open or you closed it, use the button below to reopen it."
                      />

                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 16 }}
                        message="Complete at least 72 hours before your appointment:"
                        description={
                          <>
                            <ul style={{ margin: "4px 0 8px", paddingLeft: 18 }}>
                              <li>Complete any required intake forms</li>
                              <li>Ensure you have the required plan or appointment credits</li>
                            </ul>
                            If these steps aren&apos;t completed at least 72 hours before your appointment,
                            it may be automatically cancelled so the time can be offered to another patient.
                          </>
                        }
                      />

                      <Row gutter={[16, 16]} style={{ marginTop: 22 }}>
                        <Col xs={24} md={14}>
                          <Card bordered>
                            <Title level={4} style={{ marginBottom: 12 }}>
                              Appointment details
                            </Title>
                            <div style={{ marginBottom: 10 }}>
                              <Text strong>Clinician: </Text>
                              <Text>
                                {confirmation?.providerName}
                                {confirmation?.providerRole ? ` · ${confirmation.providerRole}` : ""}
                              </Text>
                            </div>
                            {(providerMeta?.location || providerMeta?.locationLabel) && (
                              <div style={{ marginBottom: 10 }}>
                                <Text strong>Location: </Text>
                                <Text>{providerMeta.location || providerMeta.locationLabel}</Text>
                              </div>
                            )}
                            <div style={{ marginBottom: 10 }}>
                              <Text strong>When: </Text>
                              <Text type={whenLabel ? undefined : "secondary"}>
                                {whenLabel || "Pending confirmation - finish picking a time on Zoom"}
                              </Text>
                            </div>
                            <div style={{ marginBottom: 10 }}>
                              <Text strong>Meeting: </Text>
                              <Text>{confirmation?.meetingType || "—"}</Text>
                            </div>
                            <div style={{ marginTop: 10, marginBottom: 10 }}>
                              <Text strong>Link: </Text>
                              {confirmation?.meetingLink ? (
                                <a href={confirmation.meetingLink} target="_blank" rel="noreferrer">
                                  Open
                                </a>
                              ) : (
                                <Text type="secondary">Will appear once your Zoom booking is confirmed</Text>
                              )}
                            </div>
                            {reopenSchedulerUrl && (
                              <Button
                                icon={<ExportOutlined />}
                                onClick={() => window.open(reopenSchedulerUrl, "_blank", "noopener,noreferrer")}
                              >
                                Reopen scheduling page
                              </Button>
                            )}

                            <Divider />

                            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                              <Tag color={paymentOverdue ? "orange" : "green"}>
                                {hoursUntil === null
                                  ? "Payment due soon"
                                  : paymentOverdue
                                    ? `Payment due within 72h (T-${Math.max(0, Math.floor(hoursUntil))}h)`
                                    : `Payment due (T-${Math.floor(hoursUntil)}h)`}
                              </Tag>
                              <Tag color="blue">Profile & intake required next</Tag>
                              {confirmation?.confirmationCode && (
                                <Tag color="purple">Code: {confirmation.confirmationCode}</Tag>
                              )}
                            </div>
                          </Card>
                        </Col>

                        <Col xs={24} md={10}>
                          <Card bordered>
                            <Title level={4} style={{ marginBottom: 12 }}>
                              Next steps
                            </Title>
                            <div style={{ display: "grid", gap: 14 }}>
                              <div>
                                <Text strong>1) Complete payment</Text>
                                <div>
                                  <Text type="secondary">
                                    Must be completed at least 72 hours before your appointment.
                                  </Text>
                                </div>
                                <Button
                                  type="primary"
                                  style={{ width: "100%", marginTop: 10, background: "#01ADF0", borderColor: "#01ADF0" }}
                                  onClick={onCompletePayment}
                                >
                                  Complete payment
                                </Button>
                              </div>

                              <div>
                                <Text strong>2) Complete profile</Text>
                                <div>
                                  <Text type="secondary">Add ULI number and finalize account details.</Text>
                                </div>
                                <Button style={{ width: "100%", marginTop: 10 }} onClick={() => navigate("/profile")}>
                                  Complete profile
                                </Button>
                              </div>

                              <div>
                                <Text strong>3) Fill intake forms</Text>
                                <div>
                                  <Text type="secondary">Complete questionnaires to unlock your care plan.</Text>
                                </div>
                                <Button style={{ width: "100%", marginTop: 10 }} onClick={() => navigate("/questionnaire/1")}>
                                  Start intake forms
                                </Button>
                              </div>
                            </div>
                          </Card>
                        </Col>
                      </Row>

                      <div style={{ marginTop: 24, textAlign: "center" }}>
                        <Button type="link" onClick={() => navigate("/quick-book")} style={{ color: "#01ADF0" }}>
                          ← Back to booking
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Col>
          <Col span={2} className="column-3" style={{ backgroundColor: "#EFD0BD" }} />
        </Row>
      </div>
    </div>
  );
}
